import {Component, computed, DestroyRef, OnInit, inject, input, output, signal, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';

import {RadarLoaderComponent} from "../../../design-system/radar-loader/radar-loader.component";
import {BreakpointObserver} from "@angular/cdk/layout";
import {catchError, distinctUntilChanged, EMPTY, map, switchMap, timer} from "rxjs";
import {ApplicationAutodiscoverService} from "../../../../services/applications/application-autodiscover.service";
import {CurrentTenantStore} from "../../../../services/tenant/current-tenant.store";
import {tap} from "rxjs/operators";
import {ApplicationAutodiscover} from "../../../../services/applications/autodiscover.model";

import {animate, style, transition, trigger} from "@angular/animations";
import {TenantService} from "../../../../services/tenant/tenant.service";
import {ApplicationCreation, HostingType} from "../../../../services/applications/new-application.model";
import {TranslatePipe} from "@ngx-translate/core";
import {IconButtonComponent} from 'src/app/modules/design-system/icon-button/icon-button.component';
import {TextButtonComponent} from 'src/app/modules/design-system/text-button/text-button.component';
import {ApplicationIconComponent} from '../../../global/application-icon/application-icon.component';

@Component({
  selector: 'app-onboarding-step-6',
  standalone: true,
	imports: [CommonModule, IconButtonComponent, TextButtonComponent, RadarLoaderComponent, ApplicationIconComponent, TranslatePipe],
  templateUrl: './onboarding-step-6.component.html',
  styleUrl: '../onboarding.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [
		trigger('listAnimation', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate('300ms', style({ opacity: 1 }))
			]),
			transition(':leave', [
				animate('300ms', style({ opacity: 0 }))
			])
		])
	]
})
export class OnboardingStep6Component implements OnInit {
	private autoDiscoverService = inject(ApplicationAutodiscoverService);
	// Also runs outside the shell: the onboarded workspace, read once it is loaded.
	private tenantStore = inject(CurrentTenantStore);
	private tenantService = inject(TenantService);
	private destroyRef = inject(DestroyRef);

	readonly isMobile = input.required<boolean>();

	readonly onNextClick = output<string | undefined>();
	readonly onDismiss = output<undefined>();

	closeLoading = false;
	submitLoading = false;
	/** The applications could not be added: said above the button, which stays for another try (KAB-2441). */
	protected readonly addError = signal(false);

	private readonly compact = toSignal(inject(BreakpointObserver).observe(['(max-width: 1280px)']).pipe(map(state => state.matches)), {initialValue: false});
	protected readonly panelClass = computed(() => this.compact() ? '' : 'big-button');

	applications: OnboardingApplication[] = []

	private tenantId: string

	ngOnInit() {
		this.tenantStore.whenLoaded()
			.pipe(
				map(tenant => tenant.configuration.id),
				tap(tenantId => this.tenantId = tenantId),
				// Poll the cheap pending count; only reload the full inbox (Elasticsearch usage + teams)
				// when that count changes (KAB-2321). A failed poll skips the tick instead of stopping.
				switchMap(tenantId => timer(0, 2000).pipe(
					switchMap(() => this.autoDiscoverService.getApplicationAutodiscoverCount(tenantId)
						.pipe(catchError(() => EMPTY))),
					map(count => count.pending),
					distinctUntilChanged(),
					switchMap(() => this.autoDiscoverService.getAllApplicationAutodiscover(tenantId)
						.pipe(catchError(() => EMPTY)))
				)),
				takeUntilDestroyed(this.destroyRef))
			.subscribe(applications => this.setApplications(applications));
	}

	setApplications(applications: ApplicationAutodiscover[]) {
		applications.forEach((application) => {
			const app = this.applications.find(a => a.application.id === application.id)
			if (!app) {
				this.applications.push({ application, deleted: false })
			}
		})
	}

	onDelete(application: OnboardingApplication) {
		application.deleted = true
	}

	get notDeletedApplications() {
		return this.applications
			.filter(a => !a.deleted)
	}

	addTenantApplications() {
		if (this.notDeletedApplications.length > 0) {
			this.submitLoading = true

			const request: ApplicationCreation[] =
				this.notDeletedApplications
					.slice(0, 3)
					.map(a => ({
					application: {
						catalogId: a.application.id,
						name: a.application.name,
						description: a.application.description,
						hostingType: HostingType.SAAS,
						iconUrl: a.application.icon,
						autoDiscoverId: a.application.id
					}
				}))

			this.addError.set(false);
			this.tenantService.addOnboardingApplicationsToTenant(this.tenantId, request)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: () => this.onNextClick.emit("apps"),
					error: () => {
						this.submitLoading = false;
						this.addError.set(true);
					},
				})
		}
	}
}

export interface OnboardingApplication {
	application: ApplicationAutodiscover,
	deleted: boolean
}
